import "leaflet/dist/leaflet.css";

import { useEffect } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from "react-leaflet";

import { useConfig } from "../hooks/useConfig";

/** OpenStreetMap's public tiles until `map_tiles_url` is set in the public settings. */
const OSM = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

export interface MapDriver {
  id: string;
  name: string;
  lat?: number | null;
  lng?: number | null;
  active_orders: number;
  open_offer: boolean;
  vendor_name?: string | null;
}

/** Fits the view to the drivers whenever the set of positions changes. */
function FitDrivers({ drivers }: { drivers: MapDriver[] }) {
  const map = useMap();
  const key = drivers.map((d) => `${d.lat},${d.lng}`).join(";");
  useEffect(() => {
    const pts = drivers.filter((d) => d.lat != null && d.lng != null).map((d) => [d.lat as number, d.lng as number] as [number, number]);
    if (pts.length === 0) return;
    if (pts.length === 1) map.setView(pts[0], Math.max(map.getZoom(), 13));
    else map.fitBounds(pts, { padding: [30, 30], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

/** Online drivers on a map (02 §6 monitor). Markers are circles, so no icon assets ship with the bundle. */
export function DriverMap({ drivers, height = 320 }: { drivers: MapDriver[]; height?: number }) {
  const config = useConfig();
  const tiles = config?.map_tiles_url || OSM;
  const placed = drivers.filter((d) => d.lat != null && d.lng != null);
  const center: [number, number] = placed.length ? [placed[0].lat as number, placed[0].lng as number] : [13.0827, 80.2707];
  return (
    <div style={{ height, borderRadius: 10, overflow: "hidden" }}>
      <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer url={tiles} attribution="&copy; OpenStreetMap contributors" />
        <FitDrivers drivers={placed} />
        {placed.map((d) => (
          <CircleMarker
            key={d.id}
            center={[d.lat as number, d.lng as number]}
            radius={9}
            pathOptions={{
              color: "#fff",
              weight: 2,
              fillColor: d.open_offer ? "#d97706" : d.active_orders > 0 ? "#2563eb" : "#16a34a",
              fillOpacity: 0.95,
            }}
          >
            <Popup>
              <strong>{d.name}</strong>
              {d.vendor_name ? <div>{d.vendor_name}</div> : null}
              <div>
                {d.active_orders} active · {d.open_offer ? "offer pending" : "free"}
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}
